import React from "react";
import { SettingRow } from "../SettingRow";
import { Text } from "../../../../../../common/components/input/Text";
import { Toggle, Modal } from "@truevault/aquilo";
import clsx from "clsx";

interface ConsentBannerCopyOverrideRowProps {
  formApi: {
    change: (field: string, value: string) => void;
  };
  title: string;
  description?: string;
  field: string;
  resetText?: string;
  startToggled?: boolean;
}

/**
 * Generic "copy override" section that is hidden behind a toggle. Shows a warning dialog when toggled on.
 * @param formApi
 * @constructor
 */
export const ConsentBannerCopyOverrideRow = ({
  formApi,
  title,
  description,
  field,
  resetText = "",
  startToggled = false,
}: ConsentBannerCopyOverrideRowProps) => {
  const [toggled, setToggled] = React.useState<boolean>(startToggled); // Can't read from the form state yet to see if we start toggled, parent needs to determine this.
  const [revertDialogVisible, setRevertDialogVisible] = React.useState<boolean>(false);
  const [engageDialogVisible, setEngageDialogVisible] = React.useState<boolean>(false);

  const handleUserToggle = (toggled: boolean) => {
    if (!toggled) {
      // Show warning dialog before toggling off
      setRevertDialogVisible(true);
    } else {
      setEngageDialogVisible(true);
    }
  };

  const doToggleOff = () => {
    setToggled(false);
    setRevertDialogVisible(false);
    formApi.change(field, "");
  };

  const doToggleOn = () => {
    setToggled(true);
    setEngageDialogVisible(false);
    formApi.change(field, resetText);
  };

  return (
    <>
      <SettingRow maxWidthChildren={true} title={title} helpText={description}>
        {/* Toggle above text input, left-aligned */}
        <div className="tw-boundary d-flex align-items-center mb-sm" style={{ gap: "8px" }}>
          <Toggle checked={toggled} onChange={(nextChecked) => handleUserToggle(nextChecked)} />
          <span style={{ fontSize: "14px", fontWeight: 400 }}>Enable Text Customization</span>
        </div>

        {/* Show disabled text editor with default text when toggled off */}
        {!toggled && (
          <div>
            <Text
              className="w-100 mb-sm"
              fullWidth={false}
              field={`${field}_preview`}
              multiline
              rows={6}
              variant="outlined"
              helperText=""
              disabled={true}
              value={resetText}
            />
          </div>
        )}

        {/* Keep the actual form field in DOM but hidden when toggled off for React Final Form compatibility */}
        <div className={clsx(!toggled && "d-none")}>
          <Text
            className="w-100 mb-sm"
            fullWidth={false}
            field={field}
            multiline
            rows={6}
            variant="outlined"
            helperText=""
            required
          />
          {toggled && (
            <div>
              <a
                onClick={(e) => {
                  e.preventDefault();
                  handleUserToggle(false);
                }}
                href="#"
              >
                Set to Default Text
              </a>
            </div>
          )}
        </div>
      </SettingRow>
      {revertDialogVisible && (
        <Modal
          title="Banner text will revert to default"
          showOverlay
          closeOnOverlayClick={false}
          closeButton={true}
          firstButton="Keep Customized Text"
          firstButtonColor="tertiary"
          onFirstButtonClick={() => setRevertDialogVisible(false)}
          secondButton="Revert to Default"
          secondButtonColor="primary"
          onSecondButtonClick={doToggleOff}
          alignButton="right"
          alignText="left"
        >
          This will change the banner text back to the default TrueVault banner text and your
          changes will not be saved.
        </Modal>
      )}
      {engageDialogVisible && (
        <Modal
          title={`Enable ${title}?`}
          showOverlay
          closeOnOverlayClick={false}
          closeButton={true}
          firstButton="Keep Default Text"
          firstButtonColor="tertiary"
          onFirstButtonClick={() => setEngageDialogVisible(false)}
          secondButton="Customize Text"
          secondButtonColor="primary"
          onSecondButtonClick={doToggleOn}
          alignButton="right"
          alignText="left"
        >
          By using custom text for the Opt-In or Opt-Out banner, you're taking ownership of your
          on-page disclosures. We believe our standard text should serve most businesses, but if you
          have specific needs, you may proceed to customize your banner text.
        </Modal>
      )}
    </>
  );
};
